import { Button } from '@repo/ui/components'
import { snappyEaseInOut } from '@repo/ui/lib/animations'
import { AnimatePresence, motion } from 'motion/react'
import { useTranslation } from 'react-i18next'
import { HStack, Surface, Text } from '../Atoms'
import { FilterBadge } from '../Badge/PresetBadges'

export type AppliedFilter = {
  name: string
  value: unknown
  key?: string
  // Set by `useAppliedFilters`, which knows the filter behind the badge. A badge a page assembles by
  // hand has nothing to clear and so shows no dismiss button.
  onClear?: () => void
}

type AppliedFiltersBarProps = {
  appliedFilters?: AppliedFilter[]
  onBadgeClick: (filter: AppliedFilter) => void
  // Overrides the default "Applied Filters:" when the bar also carries non-filter badges.
  label?: string
  // The page's existing "reset all filters" handler. Left out, the bar shows no "Clear all" — which
  // is right for a bar carrying badges that are not all filters.
  onClearAll?: () => void
}

export function AppliedFiltersBar(props: AppliedFiltersBarProps) {
  const { t } = useTranslation('common')

  return (
    <AnimatePresence mode="wait" initial={false}>
      {props.appliedFilters && props.appliedFilters.length > 0 && (
        <motion.div
          initial={{ height: 0, marginBottom: 0, opacity: 0 }}
          animate={{ height: 'auto', marginBottom: '-0.5rem', opacity: 1 }}
          exit={{ height: 0, marginBottom: 0, opacity: 0 }}
          transition={{ duration: 0.2, ease: snappyEaseInOut }}
          className="w-full"
        >
          <Surface tone="level-2" radius="md" className="w-full p-2">
            <HStack gap="xs" align="center" className="flex-wrap">
              <Text tone="muted" size="xs" as="span" className="mr-1">
                {props.label ?? t('datatable.appliedFilters')}
              </Text>
              {props.appliedFilters.map((filter) => (
                <FilterBadge
                  key={filter.key ?? filter.name}
                  filter={filter.name}
                  value={filter.value}
                  onClick={() => props.onBadgeClick(filter)}
                  onClear={filter.onClear}
                />
              ))}

              {props.onClearAll && (
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  onClick={props.onClearAll}
                  className="ml-auto"
                >
                  {t('datatable.clearAllFilters')}
                </Button>
              )}
            </HStack>
          </Surface>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
